/**
 * The URL shapes QuarterDown parses at boot — docs/CREW-ONBOARDING.md D7.
 *
 * There is still no router and this does not add one. A fixed set of shapes is
 * recognised and the URL is never WRITTEN except for the one scrub below:
 *
 *   /c/<slug>/join#user=<key>&code=<pw>   an invite
 *   /join#user=<key>&code=<pw>            the LEGACY invite, kept alive forever
 *   /c/<slug>/join#invite=<token>         a 3026 invitation
 *   /c/<slug>/ask#key=<token>             a 3056 JOIN LINK (docs/JOIN-LINKS.md)
 *   /c/<slug>                             land on a specific crew after login
 *   /#access_token=…&type=recovery        a password-reset link (docs/EMAIL.md)
 *
 * The legacy shape is not deprecation politeness. Every invite link already
 * sitting in somebody's inbox has that shape, and phase B only started minting
 * the `/c/<slug>/` one — so dropping it would break the links that exist in
 * favour of the links that do not.
 *
 * THE RECOVERY SHAPE IS THE ONE WE DO NOT CHOOSE. The other three are minted by
 * this codebase, so their path is ours to pick; a recovery link is built by
 * GoTrue from the project's **Site URL**, which is the bare origin. So it
 * arrives on `/` with everything in the fragment, and the fragment therefore
 * has to be read whatever the path is — see `parseEntryUrl`.
 *
 * Display strings like `QUARTERDOWN.COM/C/PIZZA-DOGS` are copy, not navigation.
 */

export interface InviteCredentials {
  userKey: string;
  code: string;
}

/**
 * The session a password-reset link carries (docs/EMAIL.md §"Forgot password").
 *
 * GoTrue's implicit flow puts a REAL, ALREADY-VALID session in the fragment:
 * clicking the link is the authentication. That is why the screen it opens can
 * set a password without asking for the old one — there is no old one to ask
 * for — and why the fragment is scrubbed the instant it is read, exactly like
 * the temp password in the legacy invite shape.
 *
 * Both halves are required. An access token with no refresh token installs a
 * session that dies in an hour with no way to renew it, which is a worse
 * outcome than refusing the link.
 */
export interface RecoveryLink {
  accessToken: string;
  refreshToken: string;
}

export interface EntryUrl {
  /** The crew named in the path, or null. Lower-cased; never trusted as real. */
  slug: string | null;
  /** Credentials from the fragment, or null when there are none to use. */
  invite: InviteCredentials | null;
  /**
   * A 3056 join-link token, from `#key=<token>` on an `/ask` path.
   *
   * A SEPARATE PATH AND A SEPARATE FRAGMENT KEY FROM `inviteToken`, and the
   * separation is load-bearing rather than tidy. The two tokens mean different
   * things: `#invite=` names ONE person and is single-use, `#key=` names nobody
   * and is held by everybody the leader gave it to. Share either half and boot
   * hands a link token to `openJoin`, which peeks it as an invitation, gets
   * `invalid`, and tells the reader their invite is dead — a wrong answer that
   * reads exactly like a broken link.
   */
  linkToken: string | null;
  /**
   * A 3026 invitation token, from `#invite=<token>` (docs/INVITES.md).
   *
   * THE TWO FORMS COEXIST AND MUST NOT BE CONFLATED. `#user=&code=` is the old
   * invite: a login and a temp password, which boot SIGNS IN with immediately.
   * `#invite=` carries no credentials at all — it identifies an invitation that
   * may not have an account behind it yet, and the only thing to do with it is
   * ask the server what it is. Links of the old shape are already in people's
   * inboxes, so both are parsed for as long as those can still arrive.
   */
  inviteToken: string | null;
  /** A good password-reset link, or null. Read on ANY path — see the header. */
  recovery: RecoveryLink | null;
  /**
   * GoTrue's own refusal of an emailed link, decoded, or null.
   *
   * Worth a field of its own because the alternative is the worst screen in the
   * app: an expired reset link would drop the reader on the landing page with
   * nothing said, and they would conclude the link did nothing rather than that
   * it had timed out.
   *
   * DELIBERATELY NOT CLAIMED AS A RECOVERY ERROR. GoTrue's error fragment
   * carries no `type`, so an expired email-CHANGE confirmation is byte-identical
   * to an expired reset. The surface says the link is dead — which is true of
   * both — and does not guess which one it was.
   */
  linkError: string | null;
}

/** `^[a-z0-9-]{2,32}$`, the shape `crews.slug` is constrained to. */
const SLUG_RE = /^[a-z0-9-]{2,32}$/;

/**
 * Parse a location into the two things boot cares about. Pure: give it strings,
 * get a verdict, so the interesting cases are testable without a DOM.
 *
 * Unrecognised paths yield `{slug: null, invite: null}` rather than throwing —
 * a stray URL should land on the normal screen, not an error.
 */
export function parseEntryUrl(pathname: string, hash: string): EntryUrl {
  const path = pathname.replace(/\/+$/, '');
  let slug: string | null = null;
  let isJoin = false;
  let isAsk = false;

  /* CASE-INSENSITIVE, and that is a fix rather than politeness (2026-08-21).
     The slug ITSELF was already lower-cased below, but the `/c/` and `/join`
     around it were matched literally — so `/C/CREW-X7K4QD` parsed to nothing
     while `/c/CREW-X7K4QD` worked.

     Which would be pedantry, except THE APP PRINTS THE ADDRESS IN CAPS:
     `crewMetaTxt` renders `QUARTERDOWN.COM/C/ALPHA` because every label on
     these screens is upper-case. Copying the address the product shows you
     produced a link the product then ignored. Marcus typed exactly that form
     when he asked for this. */
  const c = /^\/c\/([^/]+)(\/join|\/ask)?$/i.exec(path);
  if (c) {
    const candidate = decodeURIComponent(c[1] ?? '').toLowerCase();
    // A slug that could not exist is treated as no slug at all: better to land
    // on the normal screen than to ask the server about `/c/..%2f..`.
    if (SLUG_RE.test(candidate)) slug = candidate;
    const tail = (c[2] ?? '').toLowerCase();
    isJoin = tail === '/join';
    isAsk = tail === '/ask';
  } else if (path.toLowerCase() === '/join') {
    isJoin = true;
  }
  // NOTE there is no bare `/ask`. The legacy `/join` shape exists because
  // links of that shape are already in people's inboxes; no join link has ever
  // been minted without a slug, so there is nothing to keep alive.

  return {
    slug,
    invite: isJoin ? parseInvite(hash) : null,
    inviteToken: isJoin ? parseInviteToken(hash) : null,
    // Gated on `/ask` and NOT on `/join`, which is the point — see `linkToken`.
    linkToken: isAsk ? parseLinkToken(hash) : null,
    // NOT gated on `isJoin`, unlike the two above, and that asymmetry is the
    // whole reason recovery needed its own parser: GoTrue builds the link from
    // the project's Site URL, so it lands on `/` and would be invisible to a
    // parser that only reads the fragment on a join path.
    recovery: parseRecovery(hash),
    linkError: parseLinkError(hash),
  };
}

/**
 * `#access_token=…&refresh_token=…&type=recovery`.
 *
 * The `type` check is what keeps this from claiming somebody else's fragment:
 * GoTrue uses the same token shape for magic links and for email-change
 * confirmations, and treating one of those as a password reset would drop the
 * reader on a set-a-new-password screen they never asked for.
 */
function parseRecovery(hash: string): RecoveryLink | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const q = new URLSearchParams(raw);
  if (q.get('type') !== 'recovery') return null;
  const accessToken = (q.get('access_token') ?? '').trim();
  const refreshToken = (q.get('refresh_token') ?? '').trim();
  // Half a session is not a session — see `RecoveryLink`.
  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

/**
 * `#error=…&error_code=…&error_description=…`, as GoTrue writes it when a link
 * is expired, already spent, or was minted for a project setting that has since
 * changed.
 *
 * The description is preferred because GoTrue's is already a sentence a person
 * can act on ("Email link is invalid or has expired"); `URLSearchParams` has
 * decoded the `+` separators by the time we read it. The codes are the fallback
 * for the versions that send no description.
 */
function parseLinkError(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const q = new URLSearchParams(raw);
  const code = (q.get('error_code') ?? '').trim();
  const err = (q.get('error') ?? '').trim();
  if (!code && !err) return null;
  const desc = (q.get('error_description') ?? '').trim();
  return desc || code || err;
}

/** `#key=<token>`, the 3056 join link. Same rule as `parseInviteToken`: no
 *  shape is imposed beyond non-empty, because the token is the server's to
 *  recognise and a client-side format check would only turn a refusal we can
 *  explain into a blank screen we cannot. */
function parseLinkToken(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const token = (new URLSearchParams(raw).get('key') ?? '').trim();
  return token || null;
}

/** `#invite=<token>`. No shape is imposed beyond non-empty: the token is the
 *  server's to recognise, and a client-side format check here would only ever
 *  turn a refusal we can explain into a blank screen we cannot. */
function parseInviteToken(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const token = (new URLSearchParams(raw).get('invite') ?? '').trim();
  return token || null;
}

/**
 * `#user=…&code=…`. Both are required — half an invite is not an invite, and
 * attempting a sign-in with a blank password would burn the honest
 * WRONG PASSWORD on a link that never carried one.
 */
function parseInvite(hash: string): InviteCredentials | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;
  const q = new URLSearchParams(raw);
  const userKey = (q.get('user') ?? '').trim();
  const code = q.get('code') ?? '';
  if (!userKey || !code) return null;
  return { userKey, code };
}

/**
 * Drop the fragment from the address bar without navigating or adding history.
 *
 * ARCHITECTURE §9 has asked for this since invites existed: the temp password
 * rides in the fragment precisely because fragments are never sent to a server,
 * but it still sits in the URL bar, in the back-forward list, and in whatever a
 * screenshot or a shoulder catches. Scrubbing it the moment it is read is the
 * difference between "not logged" and "not visible".
 *
 * A JOIN-LINK TOKEN IS THE SAME KIND OF THING AS THE TEMP PASSWORD: bearer
 * authority in the address bar, in the back-forward list, and in any
 * screenshot. It is scrubbed by the same call, on the same tick.
 *
 * A RECOVERY LINK RAISES THE STAKES rather than changing them. Its fragment is
 * not a password but a live session — anyone who replays it is signed in as
 * that account until it expires — so it is scrubbed on the same tick, by the
 * same call.
 *
 * `replaceState`, not `pushState`: the invite URL must not become a back-button
 * destination that replays the credentials.
 */
export function scrubFragment(w: {
  history?: { replaceState?: (d: unknown, t: string, u: string) => void };
  location?: { pathname?: string; search?: string };
}): void {
  const replace = w.history?.replaceState;
  if (typeof replace !== 'function') return;
  const path = (w.location?.pathname ?? '/') + (w.location?.search ?? '');
  try {
    replace.call(w.history, null, '', path);
  } catch {
    // Some embedded webviews refuse replaceState on a file: or opaque origin.
    // The sign-in still works; the fragment just stays visible.
  }
}
